import type { FeatureCollection, Geometry, MultiPolygon, Polygon } from "geojson";

/**
 * True when the geometry contains at least one numeric coordinate.
 *
 * Integration sources can produce geometry-less features — Agrian returned a
 * boundary that parsed to a `MultiPolygon` with `coordinates: []` in the
 * 2026-08-06 Bingham Ag outage. Such a feature *exists* but carries nothing
 * usable, so presence checks (e.g. "does this job have a boundary file?")
 * must look at the coordinates, not just the feature count.
 */
export function geometryHasCoordinates(geometry: Geometry | null | undefined): boolean {
	const hasNumber = (value: unknown): boolean => {
		if (typeof value === "number") return true;
		if (Array.isArray(value)) return value.some(hasNumber);
		return false;
	};
	if (!geometry) return false;
	if (geometry.type === "GeometryCollection") {
		return geometry.geometries.some(geometryHasCoordinates);
	}
	return hasNumber(geometry.coordinates);
}

/**
 * Drop features whose geometry has no coordinates; returns `undefined` when
 * nothing survives (an all-empty collection IS "no file"). A collection with
 * only valid features passes through unchanged (same reference), so callers
 * can cheaply detect "nothing was pruned".
 */
/** First [lng, lat] position in the geometry, walking past any empty polygons/
 *  rings — safe replacement for indexing `coordinates[0][0]…` blindly, which
 *  crashes on (partially) empty shapes such as Agrian's empty boundary.
 *  Returns null when the geometry carries no positions at all. */
export function firstPositionOfGeometry(geom: Polygon | MultiPolygon): [number, number] | null {
	const coordSets = geom.type === "Polygon" ? [geom.coordinates] : geom.coordinates;
	for (const polygon of coordSets) {
		for (const ring of polygon) {
			for (const coord of ring) {
				if (coord.length >= 2) return [coord[0], coord[1]];
			}
		}
	}
	return null;
}

export function pruneEmptyGeometryFeatures(collection: FeatureCollection | undefined): FeatureCollection | undefined {
	if (!collection) return undefined;
	const features = collection.features ?? [];
	const kept = features.filter((feature) => geometryHasCoordinates(feature.geometry));
	if (kept.length === 0) return undefined;
	if (kept.length === features.length) return collection;
	return { ...collection, features: kept };
}
